import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Globe, User } from 'lucide-react';
import { Button } from './ui/button';
import { useLang } from '../contexts/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_84027868-605e-4a17-95ea-ac9185a5f86d/artifacts/v7atgd7h_Impulsodigital2030%40gmail.com_20260410_215902_0000.png";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, toggleLang, t } = useLang();

  const navLinks = [
    { href: '/', label: t('nav_home') },
    { href: '/#services', label: t('nav_services') },
    { href: '/#destinations', label: t('nav_destinations') },
    { href: '/#excursions', label: t('nav_excursions') },
    { href: '/#vehicles', label: t('nav_vehicles') },
    { href: '/#contact', label: t('nav_contact') },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsMobileMenuOpen(false); }, [location]);

  const scrollToSection = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0A192F]/90 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img src={LOGO_URL} alt="Ferr Tours" className="h-20 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium uppercase tracking-wider"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/20 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              data-testid="lang-toggle"
            >
              <Globe className="w-4 h-4 text-cyan-400" />
              {lang === 'es' ? 'EN' : 'ES'}
            </button>
            <a
              href="https://wa.me/18299838580"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors"
              data-testid="header-whatsapp"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+1 (829) 983-8580</span>
            </a>
            <Link to="/#booking">
              <Button
                className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold uppercase tracking-wide hover:brightness-110 transition-all shadow-[0_0_20px_rgba(234,88,12,0.4)] px-6"
                data-testid="header-book-now-btn"
                onClick={(e) => scrollToSection(e, '/#booking')}
              >
                {t('book_now')}
              </Button>
            </Link>
            <Link
              to="/admin"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/20 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              data-testid="header-admin-btn"
            >
              <User className="w-4 h-4 text-cyan-400" />
              Admin
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <Link
              to="/admin"
              className="flex items-center gap-1 px-2 py-1 rounded-full border border-white/20 text-xs font-medium text-white"
              data-testid="header-admin-btn-mobile"
            >
              <User className="w-3.5 h-3.5 text-cyan-400" />
            </Link>
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-2 py-1 rounded-full border border-white/20 text-xs font-medium text-white"
              data-testid="lang-toggle-mobile"
            >
              <Globe className="w-3.5 h-3.5 text-cyan-400" />
              {lang === 'es' ? 'EN' : 'ES'}
            </button>
            <button
              className="p-2 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-20 bg-[#0A192F]/98 backdrop-blur-xl z-40">
          <nav className="flex flex-col items-center justify-center h-full space-y-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-2xl text-slate-200 hover:text-white transition-colors font-medium"
              >
                {link.label}
              </a>
            ))}
            <Link to="/#booking">
              <Button
                className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold uppercase tracking-wide px-8 py-3 text-lg"
                onClick={(e) => scrollToSection(e, '/#booking')}
              >
                {t('book_now')}
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
